import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import { markerSvgFor } from './propertyMap';
import {
  clusterPins,
  initialFocusBounds,
  MapBounds,
  MapPin,
  PinCluster,
} from './propertyClusters';
import { shade } from '../../styles/theme';

interface Props {
  pins: MapPin[];
  selectedId?: string | null;
  onSelect: (pin: MapPin) => void;
  onBoundsChange: (bounds: MapBounds) => void;
  /** Onde abrir quando o catálogo ainda não tem nenhum ponto. Vem do endereço
   *  do próprio tenant: a plataforma é multi-tenant e cada imobiliária está
   *  numa cidade diferente. */
  fallbackCenter: [number, number];
}

const TILE_URL =
  process.env.REACT_APP_MAP_TILE_URL || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION =
  process.env.REACT_APP_MAP_TILE_ATTRIBUTION ||
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const Frame = styled.div`
  position: relative;
  height: 100%;
  min-height: 420px;

  .leaflet-container {
    height: 100%;
    width: 100%;
    font-family: inherit;
  }

  .pin-marker,
  .cluster-marker {
    background: transparent;
    border: none;
    cursor: pointer;
  }

  .pin-marker span {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border-radius: 50% 50% 50% 4px;
    transform: rotate(-45deg);
    background: ${({ theme }) => shade(theme.colors.primary, -0.45)};
    color: ${({ theme }) => theme.colors.onPrimary};
    border: 3px solid #fff;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.35);
    transition: transform 0.16s ease;
  }

  .pin-marker svg { transform: rotate(45deg); width: 19px; height: 19px; }

  .pin-marker:hover span { transform: rotate(-45deg) scale(1.12); }

  .pin-marker.is-selected span {
    background: ${({ theme }) => theme.colors.foreground};
    transform: rotate(-45deg) scale(1.16);
  }

  /* O agrupamento cresce com a quantidade: 3 imóveis e 30 não podem ter o
     mesmo peso visual na hora de escolher para onde olhar. */
  .cluster-marker span {
    display: grid;
    place-items: center;
    border-radius: 50%;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.onPrimary};
    background: ${({ theme }) => shade(theme.colors.primary, -0.45)};
    border: 3px solid #fff;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.35);
  }
`;

const Hint = styled.div`
  position: absolute;
  z-index: 500;
  left: 50%;
  bottom: 14px;
  transform: translateX(-50%);
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 0.8125rem;
  background: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.text.secondary};
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.18);
  pointer-events: none;
`;

function clusterIcon(count: number, background: string) {
  const size = count >= 25 ? 54 : count >= 10 ? 48 : 42;
  return L.divIcon({
    className: 'cluster-marker',
    html: `<span style="width:${size}px;height:${size}px;font-size:${
      size >= 48 ? 16 : 14
    }px;background:${background}">${count}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function pinIcon(pin: MapPin, selected: boolean) {
  return L.divIcon({
    className: `pin-marker${selected ? ' is-selected' : ''}`,
    html: `<span>${markerSvgFor(pin.type)}</span>`,
    iconSize: [38, 38],
    iconAnchor: [19, 36],
  });
}

/** Reporta zoom e janela visível para quem desenha os agrupamentos e a lista. */
const ViewportWatcher: React.FC<{
  onChange: (bounds: MapBounds, zoom: number) => void;
}> = ({ onChange }) => {
  const map = useMap();

  const report = React.useCallback(() => {
    const bounds = map.getBounds();
    onChange(
      {
        south: bounds.getSouth(),
        north: bounds.getNorth(),
        west: bounds.getWest(),
        east: bounds.getEast(),
      },
      map.getZoom()
    );
  }, [map, onChange]);

  useEffect(report, [report]);
  useMapEvents({ moveend: report, zoomend: report });
  return null;
};

/** Enquadra o catálogo na primeira carga, e só nela: reenquadrar a cada
 *  filtro jogaria fora a área que a pessoa escolheu olhar. */
const FitToPins: React.FC<{ pins: MapPin[] }> = ({ pins }) => {
  const map = useMap();
  const done = useRef(false);

  useEffect(() => {
    if (done.current || pins.length === 0) return;
    const focus = initialFocusBounds(pins);
    if (!focus) return;
    done.current = true;
    map.fitBounds(
      L.latLngBounds([focus.south, focus.west], [focus.north, focus.east]),
      { padding: [48, 48], maxZoom: 15 }
    );
  }, [map, pins]);

  return null;
};

const PropertySearchMap: React.FC<Props> = ({
  pins,
  selectedId,
  onSelect,
  onBoundsChange,
  fallbackCenter,
}) => {
  const theme = useTheme();
  const [zoom, setZoom] = useState(13);
  const mapRef = useRef<L.Map | null>(null);

  const clusters = useMemo(() => clusterPins(pins, zoom), [pins, zoom]);
  const clusterColor = shade(theme.colors.primary, -0.45);

  const handleViewport = React.useCallback(
    (bounds: MapBounds, nextZoom: number) => {
      setZoom(nextZoom);
      onBoundsChange(bounds);
    },
    [onBoundsChange]
  );

  const openCluster = (cluster: PinCluster) => {
    const map = mapRef.current;
    if (!map) return;
    const bounds = L.latLngBounds(
      cluster.pins.map(p => [p.latitude, p.longitude] as [number, number])
    );
    // Imóveis na mesma coordenada (um condomínio inteiro) nunca se separam por
    // zoom: nesse caso o grupo vira seleção na lista, que é onde cabem 24 cards.
    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
      onSelect(cluster.pins[0]);
      map.setView([cluster.latitude, cluster.longitude], Math.max(map.getZoom(), 16));
      return;
    }
    map.fitBounds(bounds, { padding: [56, 56] });
  };

  return (
    <Frame>
      <MapContainer
        center={fallbackCenter}
        zoom={13}
        scrollWheelZoom
        ref={instance => {
          mapRef.current = instance;
        }}
      >
        <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
        <ViewportWatcher onChange={handleViewport} />
        <FitToPins pins={pins} />

        {clusters.map(cluster =>
          cluster.pins.length === 1 ? (
            <Marker
              key={cluster.pins[0].id}
              position={[cluster.pins[0].latitude, cluster.pins[0].longitude]}
              icon={pinIcon(cluster.pins[0], cluster.pins[0].id === selectedId)}
              eventHandlers={{ click: () => onSelect(cluster.pins[0]) }}
            />
          ) : (
            <Marker
              key={cluster.id}
              position={[cluster.latitude, cluster.longitude]}
              icon={clusterIcon(cluster.pins.length, clusterColor)}
              eventHandlers={{ click: () => openCluster(cluster) }}
            />
          )
        )}
      </MapContainer>

      <Hint>Arraste o mapa para buscar por região</Hint>
    </Frame>
  );
};

export default PropertySearchMap;
